import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { MembershipRoleRow, MembershipRoleType, MembershipRow, OrganizationRow, ProfileRow, SettingsRow, SharingMinistryType, SmiPlan, SubscriptionRow } from '../types/supabase'
import type { DeepNullable, NotNull } from '../types/util'
import { PURGE } from 'redux-persist';
import pick from 'lodash/pick'
import { redactName, redactObjectExceptKeys } from '../lib/util/redact'
import { keysPresent } from '../lib/util/present'

export type ProfileModel = {
  id: string
  updated_at?: string | null | undefined
  full_name: string
  date_of_birth: string

  // Nullable: only name and date of birth are required to have a usable profile
  // (see `isFilledProfile`). These are collected on the profile screen and
  // backfilled from the CHM forms, so they are absent for anyone who hasn't
  // reached either yet.
  address?: string | null | undefined
  phone?: string | null | undefined
  phone_type?: string | null | undefined
  signature_data?: string | null | undefined
  ssn_last_4?: string | null | undefined

  // Additional email addresses the user uses with providers, separate from
  // their sign-in email (auth.users.email). Optional on the model so that
  // callers loading from persisted Redux state across a deploy don't see
  // TypeErrors; consumers should defensive-guard with `?? []`.
  alternate_emails?: string[]

  onboarding_step_completed_at?: string | null | undefined
  onboarding_steps_completed?: string[] | null | undefined
  account_deletion_requested_at?: string | null | undefined
}

/**
 * Returns true if the profile identifies the member well enough to use.
 *
 * Only name + date of birth: together they are what makes the member selectable
 * as a patient (see `dependentsSlice.onSyncDependents` and `selectPatientsByUnit`),
 * which is the one thing the app can't work around. Phone and address are
 * collected on the profile screen and prefilled into CHM forms and AI calls when
 * present, but every one of those consumers tolerates their absence, so requiring
 * them here only had the effect of hiding the whole profile from the app.
 */
export const isFilledProfile = keysPresent('full_name', 'date_of_birth')

export const OnboardingFlowStates = [
  'setMembership',
  'fillProfile',
  'consent',
  'enableTwoFactor',
  'startTour'
] as const

export type OnboardingFlowState = (typeof OnboardingFlowStates)[number]

// Steps an invited spouse should not see during onboarding. They were added to
// an existing membership, so "choose your membership" is already done. See
// `isInvitedSpouse`. (Inviting a spouse of their own is offered inline on the
// `fillProfile` step rather than as a separate flow state — see `fillProfile.tsx`
// — and is hidden there for invited spouses using the same `isInvitedSpouse` check.)
//
// NOTE: `hasCompletedOnboarding` requires EVERY step in `OnboardingFlowStates`
// to be present in `onboarding_steps_completed`. The onboarding screen satisfies
// that for spouses by writing these skipped steps as completed even though it
// never shows them. So when you add a new step to `OnboardingFlowStates` that an
// invited spouse should not have to complete, you must also add it here —
// otherwise spouses can never finish onboarding.
export const INVITED_SPOUSE_SKIPPED_STEPS: readonly OnboardingFlowState[] = [
  'setMembership',
]

// Original onboarding steps, from before `enableTwoFactor` and `consent` (and
// the steps those replaced) existed. Users who completed onboarding against
// this original list should not be re-onboarded just because the flow grew new
// steps afterward — they get those offers from the settings screen instead.
// This fallback covers every step added since launch, not only 2FA: keep this
// list exactly as it always was rather than adding new steps to it.
//
// Note: this fallback also makes onboarding "completable" for new users who URL-jump
// past an optional step. That is intentionally tolerated — onboarding completion is not
// a security boundary. The actual 2FA enforcement happens in `MfaStepUpGate` at sign-in,
// which only triggers when the user has a verified factor. Skipping the offer just means
// the user opts out, equivalent to clicking "Skip for now" in the UI.
const LegacyOnboardingFlowStates = [
  'setMembership',
  'fillProfile',
  'startTour'
] as const

export function hasCompletedOnboarding({onboarding_steps_completed}: Pick<ProfileRow, 'onboarding_steps_completed'>) {
  if (!onboarding_steps_completed) { return false }

  const completedAll = OnboardingFlowStates.every((step) => onboarding_steps_completed.includes(step))
  if (completedAll) { return true }

  return LegacyOnboardingFlowStates.every((step) => onboarding_steps_completed.includes(step))
}

/**
 * Returns true if the current user was invited onto a membership they did not
 * create (e.g. an invited spouse co-admin). Such users had their membership
 * chosen for them and were themselves the invitee, so the "choose membership"
 * and "invite spouse" onboarding steps don't apply.
 *
 * Returns false when `membershipCreatedByUserId` is missing — it can be absent
 * on state rehydrated from a pre-deploy redux-persist snapshot (CLAUDE.md
 * "Redux Persist and Stale Schema"). Defaulting to false means we never wrongly
 * skip steps for a primary member. During onboarding the slice is freshly
 * synced from the server, so the field is present for real invited spouses.
 */
export function isInvitedSpouse(
  state: Pick<MembershipSliceState, 'userId' | 'membershipId' | 'membershipCreatedByUserId'>
): boolean {
  if (!state.membershipId || !state.membershipCreatedByUserId) { return false }
  return state.membershipCreatedByUserId !== state.userId
}

export type MembershipSliceState = {
  uninitialized?: false

  // These are intentionally marked as non-nullable because the initializer
  // ensures that these are set, otherwise it takes you to onboarding.  So
  // the rest of the app doesn't need to worry if these are null or not
  userId: string
  fullName: string
  onboardingCompletedAt: number
  membershipId: string
  member_number: string | null,
  sharing_ministry_type: SharingMinistryType,
  iua_amount: number | null,
  smi_plan: SmiPlan | null,
  role: MembershipRoleType

  // The user who created the membership. When this differs from `userId`, the
  // current user was added to someone else's membership (e.g. an invited
  // spouse) rather than founding their own. Optional because it was added after
  // launch — objects rehydrated from persisted Redux state across the deploy
  // won't have it until the next server sync (CLAUDE.md "Redux Persist and
  // Stale Schema"); guard with `isInvitedSpouse`, which treats absence as
  // "not an invited spouse".
  membershipCreatedByUserId?: string | null

  // When the member affirmatively consented to Alice handling their health
  // information over email and other notifications; null when they never have or
  // have since opted back out. Optional because it was added after launch — state
  // rehydrated from a pre-deploy redux-persist snapshot won't have it until the next
  // server sync (CLAUDE.md "Redux Persist and Stale Schema"). `undefined` and `null`
  // both mean "no consent on record", which is the safe reading either way.
  aliceHealthDataConsentAt?: string | null

  profile: ProfileModel | null
  settings: SettingsRow | null

  organization: Pick<OrganizationRow, 'id' | 'name' | 'customizations' | 'feature_flags' | 'sharing_ministry_type' | 'subscription_end_date' | 'created_by_user_id'> | null

  subscription: Pick<SubscriptionRow, 'id' | 'status' | 'current_period_end' | 'cancel_at_period_end' | 'tier'> | null

  /**
   * The server's verdict on whether this member's access has lapsed, from
   * SubscriptionLimitService::isSubscriptionRequired via GET /backend/memberships/mine.
   *
   * Optional on purpose, and `undefined` is meaningful: state persisted before this
   * field shipped won't have it until the next startup sync completes (CLAUDE.md,
   * "Redux Persist and Stale Schema"). isSubscriptionReadOnly falls back to deciding
   * locally in that case rather than treating the absence as "access is fine".
   */
  subscriptionRequired?: boolean

  /**
   * Per-membership native-app grant (`memberships.native_app_shell`).
   *
   * Optional because it was added after launch — state rehydrated from a
   * pre-deploy redux-persist snapshot won't have it until the next startup
   * sync (CLAUDE.md "Redux Persist and Stale Schema"). Absence means the
   * membership has not been granted the shell; the organization flag is
   * checked separately and can still turn it on.
   */
  nativeAppShell?: boolean
}

/**
 * The result of a Supabase query for the user's current membership info
 */
export type OnSyncMembershipPayload = {
  userId: string,
  membership: MembershipRow,
  role: MembershipRoleRow
  profile?: ProfileRow | null
  settings?: SettingsRow

  organization: Pick<OrganizationRow, 'id' | 'name' | 'customizations' | 'feature_flags' | 'sharing_ministry_type' | 'subscription_end_date' | 'created_by_user_id'> | null

  subscription?: SubscriptionRow | null

  subscriptionRequired?: boolean
}

export interface CreateMembershipPayload {
  membership: MembershipRow,
  role: MembershipRoleRow
}

/**
 * A `saveMyMembership` response: the membership row and the caller's role.
 *
 * The profile is not in that response, and the organization on it is not the
 * one startup already loaded. `onSyncMembership` rebuilds the slice from
 * whatever it is given, so a save dispatched through it nulls both until the
 * next full load.
 */
export interface OnSaveMembershipPayload {
  userId?: string
  membership: MembershipRow
  role?: MembershipRoleRow
  /**
   * The organization the save resolved. Adopted only while the slice has none:
   * a brand-new member's startup load found no membership and so no
   * organization, and without this the org's feature flags stayed off until
   * the next launch. An organization startup already loaded is left alone.
   */
  organization?: OnSyncMembershipPayload['organization']
}

const initialState = {
  uninitialized: true
} as unknown as MembershipSliceState

const membershipSlice = createSlice({
  name: 'membership',
  initialState: {...initialState},
  reducers: {
    /**
     * Dispatched by the initializer when the state is loaded from the Supabase server
     */
    onSyncMembership(state, action: PayloadAction<OnSyncMembershipPayload>) {
      const {userId, membership, role, profile, settings} = action.payload

      let onboardingCompletedAt: number | undefined
      if (profile) {
        if (hasCompletedOnboarding(profile) && profile.onboarding_step_completed_at) {
          onboardingCompletedAt = Date.parse(profile.onboarding_step_completed_at)
        }
      }

      // reset the state
      return {
        userId,
        // See comment above as to why these are marked non-null
        membershipId: membership?.id || state.membershipId,
        membershipCreatedByUserId: membership?.created_by_user_id ?? state.membershipCreatedByUserId ?? null,
        member_number: membership?.member_number ?? state.member_number ?? null,
        sharing_ministry_type: (membership?.sharing_ministry_type as SharingMinistryType) ?? state.sharing_ministry_type ?? null,
        iua_amount: membership?.iua_amount ?? state.iua_amount ?? null,
        smi_plan: (membership?.smi_plan as SmiPlan | null) ?? state.smi_plan ?? null,
        // Read straight from the server row rather than falling back to the
        // previous state: a revocation elsewhere (Alice, another device) has to be
        // able to clear it, and `??` would keep the stale grant.
        aliceHealthDataConsentAt: membership
          ? (membership.alice_health_data_consent_at ?? null)
          : (state.aliceHealthDataConsentAt ?? null),
        role: role?.role || state.role,
        fullName: profile?.full_name! || state.fullName,
        profile: (profile && isFilledProfile(profile)) ? profile : null,
        settings: settings || state.settings,
        onboardingCompletedAt: onboardingCompletedAt || state.onboardingCompletedAt,
        organization: action.payload.organization ? pick(action.payload.organization, 'id', 'name', 'customizations', 'feature_flags', 'sharing_ministry_type', 'subscription_end_date', 'created_by_user_id') : null,
        subscription: action.payload.subscription ? pick(action.payload.subscription, 'id', 'status', 'current_period_end', 'cancel_at_period_end', 'tier') : (state.subscription || null),
        // Presence of the key, not its value, decides. A payload that carries it is
        // speaking for the server, so `false` must be able to clear a previous `true`
        // when the member resubscribes -- `?? state.subscriptionRequired` would pin the
        // stale "expired" answer in place. `undefined` from a backend that predates the
        // field is equally authoritative: it drops us onto the local fallback, which is
        // what that older server implies.
        //
        // A payload that omits the key entirely has no opinion. The onboarding
        // dispatches in setMembershipDetails.tsx build their payload from a local
        // membership write and never see the verdict, so assigning unconditionally there
        // would wipe what startup had just established.
        subscriptionRequired: 'subscriptionRequired' in action.payload
          ? action.payload.subscriptionRequired
          : state.subscriptionRequired,
        // `=== true` so a row that predates the column, or a payload that
        // omits it, reads as not granted. A later sync that says false has
        // to be able to clear a previous true, so this does not fall back
        // to the previous slice value.
        nativeAppShell: membership ? membership.native_app_shell === true : false,
      }
    },
    /**
     * Applies a membership save (`saveMyMembership`) without a full resync.
     * `onSyncMembership` rebuilds the whole slice, so feeding it only these rows
     * drops the profile and organization the screen is showing. This touches the
     * membership columns and leaves everything else alone.
     */
    onSaveMembership(state, action: PayloadAction<OnSaveMembershipPayload>) {
      const {userId, membership, role, organization} = action.payload

      if (organization && !state.organization) {
        state.organization = pick(organization, 'id', 'name', 'customizations', 'feature_flags', 'sharing_ministry_type', 'subscription_end_date', 'created_by_user_id')
      }
      if (userId) {
        state.userId = userId
      }
      if (membership) {
        if (membership.id) {
          state.membershipId = membership.id
        }
        state.membershipCreatedByUserId = membership.created_by_user_id ?? state.membershipCreatedByUserId ?? null
        // Assigned, not `?? state`: choosing "no ministry" sends nulls, and
        // falling back would keep the number the member just cleared.
        state.member_number = membership.member_number ?? null
        state.sharing_ministry_type = (membership.sharing_ministry_type as SharingMinistryType) ?? null
        state.iua_amount = membership.iua_amount ?? null
        state.smi_plan = (membership.smi_plan as SmiPlan | null) ?? null
        // Absent on a partial row (a test double, an older client). A present
        // null is a real "never consented" and has to be writable.
        if ('alice_health_data_consent_at' in membership) {
          state.aliceHealthDataConsentAt = membership.alice_health_data_consent_at ?? null
        }
        if ('native_app_shell' in membership) {
          state.nativeAppShell = membership.native_app_shell === true
        }
      }
      if (role?.role) {
        state.role = role.role
      }
      // The pre-sync placeholder is the only state that carries this flag.
      // A saved membership is one the rest of the app can read.
      if ('uninitialized' in state) {
        delete (state as { uninitialized?: boolean }).uninitialized
      }
    },
    onUpdateMemberNumber(state, action: PayloadAction<{ member_number: string }>) {
      state.member_number = action.payload.member_number
    },
    setProfile(state, action: PayloadAction<{ profile: ProfileModel }>) {
      const {profile} = action.payload
      state.profile = profile
      state.fullName = profile.full_name
    },
    
    setSettings(state, action: PayloadAction<{ settings: SettingsRow }>) {
      const {settings} = action.payload
      state.settings = settings
    },

    /**
     * Records the member's affirmative consent (or its withdrawal) for Alice to
     * handle their health information over email. Email only — push notifications
     * carry no content to consent to.
     */
    setAliceHealthDataConsent(state, action: PayloadAction<{ consentedAt: string | null }>) {
      state.aliceHealthDataConsentAt = action.payload.consentedAt
    },
    /**
     * Creates a new membership, setting this user as Admin
     */
    createMembership(state, action: PayloadAction<CreateMembershipPayload>) {
      const { membership, role } = action.payload
      state.member_number = membership.member_number
      state.sharing_ministry_type = (membership.sharing_ministry_type as SharingMinistryType) ?? null
      state.membershipId = membership.id
      state.membershipCreatedByUserId = membership.created_by_user_id ?? null
      state.role = role.role
    },
    setSubscription(state, action: PayloadAction<{ subscription: SubscriptionRow | null }>) {
      state.subscription = action.payload.subscription
        ? pick(action.payload.subscription, 'id', 'status', 'current_period_end', 'cancel_at_period_end', 'tier')
        : null
    }
  },
  extraReducers: (builder) => {
    builder = builder.addCase(PURGE, (state) => {
      return initialState
    })

    return builder
  }
})

// Action creators are generated for each case reducer function
export const { onSyncMembership, onSaveMembership, onUpdateMemberNumber, setProfile, setSettings, setAliceHealthDataConsent, createMembership, setSubscription } = membershipSlice.actions

/**
 * Returns true if the user's subscription has expired and the app should be read-only.
 *
 * The server decides this -- SubscriptionLimitService::isSubscriptionRequired, delivered
 * as `subscriptionRequired` by GET /backend/memberships/mine. It is the same verdict that
 * gates Alice, emailed receipts and receipt OCR on the backend, so honoring it here is
 * what keeps the UI from claiming full access while those are switched off. It also
 * covers the 30-day trial fallback for a member who never had a `subscriptions` row,
 * which this side has no way to work out for itself.
 *
 * The local rules below are only a fallback for state persisted before the server
 * started sending its verdict -- one startup sync replaces them. Keep them in step with
 * the PHP rather than extending them; new rules belong in SubscriptionLimitService.
 */
export function isSubscriptionReadOnly(state: MembershipSliceState): boolean {
  if (typeof state.subscriptionRequired === 'boolean') {
    return state.subscriptionRequired
  }

  // Users with an organization check org subscription status
  if (state.organization) {
    const { subscription_end_date } = state.organization
    if (subscription_end_date) {
      if (new Date(subscription_end_date) > new Date()) {
        // Org subscription still active - full access
        return false
      }
      // Org subscription expired - check personal subscription below
      // If no personal subscription, user should be read-only (not trial state)
      if (!state.subscription) {
        return true
      }
    } else {
      // Orgs without a subscription end date have full access indefinitely
      return false
    }
  }

  // No organization AND no subscription = pre-sign-up trial state
  // Allow full access; subscription prompts will encourage users to subscribe
  if (!state.subscription) { return false }

  const { status, current_period_end } = state.subscription
  // Active or trialing subscriptions with a future end date are fine
  if ((status === 'active' || status === 'trialing') && new Date(current_period_end) > new Date()) {
    return false
  }

  // Any other status (canceled, past_due, unpaid, etc.) is read-only
  return true
}

export default membershipSlice.reducer

export function redactMembership(state: MembershipSliceState): DeepNullable<MembershipSliceState> {
  return {
    ...pick(state, 'userId', 'membershipId', 'membershipCreatedByUserId', 'role',
      'onboardingCompletedAt', 'aliceHealthDataConsentAt', 'subscriptionRequired'),
    fullName: redactName(state?.fullName),
    member_number: 'REDACTED',
    sharing_ministry_type: state.sharing_ministry_type,
    iua_amount: state.iua_amount,
    smi_plan: state.smi_plan,
    profile: null,
    organization: state.organization ?
      redactObjectExceptKeys(state.organization, 'id') :
      null,
    settings: state.settings ?
      redactObjectExceptKeys(state.settings,
        'id', 'user_id', 'created_at', 'updated_at', 'textract_opt_in', 'conversation_data_opt_out') :
      null,
    subscription: state.subscription ?
      pick(state.subscription, 'id', 'status', 'current_period_end', 'cancel_at_period_end', 'tier') :
      null
  }
}
